export type SocialAuthProvider = 'google' | 'facebook' | 'github';

export type SocialAuthFlow = 'login' | 'register';

export type SocialAuthPendingContext = {
  flow: SocialAuthFlow;
  provider: SocialAuthProvider;
  role?: 'guardian' | 'academy_owner' | 'coach';
  returnUrl?: string | null;
  pendingBookingId?: string | null;
  planId?: string | null;
};

const PENDING_KEY = 'courtsync_social_auth_pending';
const READY_KEY = 'courtsync_social_auth_ready';
const ERROR_KEY = 'courtsync_social_auth_error';

export class SocialAuthRedirectInitiatedError extends Error {
  readonly code = 'auth/redirect-initiated';

  constructor() {
    super('Redirecting for sign-in…');
    this.name = 'SocialAuthRedirectInitiatedError';
  }
}

export function isSocialAuthRedirectInitiatedError(error: unknown): boolean {
  return (
    error instanceof SocialAuthRedirectInitiatedError ||
    (error as { code?: string })?.code === 'auth/redirect-initiated'
  );
}

/**
 * Popup OAuth relies on window.opener messaging. Embedded browsers (Cursor, Electron,
 * iframes) often open the provider UI in a separate window that cannot hand control
 * back to the opener — use full-page redirect instead.
 */
export function shouldUseSocialAuthRedirect(): boolean {
  if (typeof window === 'undefined') return false;

  if (window.self !== window.top) return true;

  const ua = navigator.userAgent || '';
  if (/Electron/i.test(ua)) return true;
  if (/Cursor/i.test(ua)) return true;
  if (/WebView/i.test(ua)) return true;

  return false;
}

export function isPopupAuthFailure(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  return (
    code === 'auth/popup-blocked' ||
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request'
  );
}

export function prepareSocialAuthContext(context: SocialAuthPendingContext): void {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(context));
  sessionStorage.removeItem(READY_KEY);
  sessionStorage.removeItem(ERROR_KEY);
}

export function readSocialAuthPending(): SocialAuthPendingContext | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as SocialAuthPendingContext;
  } catch {
    sessionStorage.removeItem(PENDING_KEY);
    return null;
  }
}

export function clearSocialAuthPending(): void {
  sessionStorage.removeItem(PENDING_KEY);
  sessionStorage.removeItem(READY_KEY);
  sessionStorage.removeItem(ERROR_KEY);
}

export function markSocialAuthRedirectReady(): void {
  sessionStorage.setItem(READY_KEY, '1');
  sessionStorage.removeItem(ERROR_KEY);
}

export function markSocialAuthRedirectFailed(message: string): void {
  sessionStorage.removeItem(READY_KEY);
  sessionStorage.setItem(ERROR_KEY, message);
}

export function readSocialAuthRedirectError(): string | null {
  const message = sessionStorage.getItem(ERROR_KEY);
  if (!message) return null;
  sessionStorage.removeItem(ERROR_KEY);
  return message;
}

export type ConsumedSocialAuthRedirect = {
  pending: SocialAuthPendingContext | null;
  /** True when a social redirect completed successfully, even if pending context was lost. */
  hadRedirect: boolean;
};

export function consumeSocialAuthRedirectIfReady(): ConsumedSocialAuthRedirect | null {
  if (sessionStorage.getItem(READY_KEY) !== '1') return null;

  sessionStorage.removeItem(READY_KEY);
  const pending = readSocialAuthPending();
  sessionStorage.removeItem(PENDING_KEY);
  return { pending, hadRedirect: true };
}
